import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, type Location } from "react-router-dom";

import { Fonts } from "@snapchat/snap-design-system";
import ReactRouterPrompt from "react-router-prompt";

import { useModal } from "../../core/hooks";
import { modalStyle } from "../styles";

import { ActionModal } from "./ActionModal";

export interface UnsavedChangesPromptProps {
    when: boolean;
    cancelText?: ReactNode;
    actionText?: ReactNode;
}

export const UnsavedChangesPrompt = ({
    when,
    cancelText = "Cancel",
    actionText = "Continue",
}: UnsavedChangesPromptProps) => {
    const navigate = useNavigate();
    const [lastBlockedLocation, setLastBlockedLocation] = useState<Location | null>(null);
    const [modalProps] = useModal();

    const beforeUnloadEventListener = useCallback((event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = "Warning, you have unsaved changes";
    }, []);

    useEffect(() => {
        if (lastBlockedLocation) {
            navigate(lastBlockedLocation.pathname);
            setLastBlockedLocation(null);
        }
    }, [navigate, lastBlockedLocation]);

    useEffect(() => {
        if (when) {
            window.addEventListener("beforeunload", beforeUnloadEventListener);
        } else {
            window.removeEventListener("beforeunload", beforeUnloadEventListener);
        }
        return () => {
            window.removeEventListener("beforeunload", beforeUnloadEventListener);
        };
    }, [when, beforeUnloadEventListener]);

    return (
        <>
            <ReactRouterPrompt when={when}>
                {({ isActive, onCancel, onConfirm }) => (
                    <ActionModal
                        {...modalProps}
                        visible={isActive}
                        onCancel={onCancel}
                        className={modalStyle}
                        title="Warning, you have unsaved changes"
                        cancelText={cancelText}
                        actionText={actionText}
                        onAction={onConfirm}
                    >
                        <span style={Fonts.Paragraph.P200}>All changes will be lost if you continue.</span>
                    </ActionModal>
                )}
            </ReactRouterPrompt>
        </>
    );
};
